class FeatureCardGrid extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-feature-card-grid-list]');
    this.items = Array.from(this.querySelectorAll('[data-feature-card-grid-item]'));
    this.indicators = Array.from(this.querySelectorAll('[data-feature-card-grid-indicator]'));

    if (!(this.list instanceof HTMLElement) || this.items.length < 2 || this.indicators.length < 2) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleIndicatorClick = this.handleIndicatorClick.bind(this);

    this.list.addEventListener('scroll', this.handleScroll, { passive: true });
    this.indicators.forEach((indicator) => indicator.addEventListener('click', this.handleIndicatorClick));
    window.addEventListener('resize', this.handleResize);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.updateActiveIndicator();
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.handleScroll);
    this.indicators?.forEach((indicator) => indicator.removeEventListener('click', this.handleIndicatorClick));
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);

    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = undefined;
    }
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      this.updateActiveIndicator();
    });
  }

  handleResize() {
    this.handleScroll();
  }

  handleBlockSelect(event) {
    if (!(event.target instanceof Element)) return;

    const selectedItem = event.target.closest('[data-feature-card-grid-item]');

    if (!(selectedItem instanceof HTMLElement) || !this.contains(selectedItem)) return;

    this.scrollToItem(this.items.indexOf(selectedItem));
  }

  handleIndicatorClick(event) {
    if (!(event.target instanceof Element)) return;

    const selectedIndicator = event.target.closest('[data-feature-card-grid-indicator]');
    const selectedIndex = this.indicators.indexOf(selectedIndicator);

    if (selectedIndex < 0) return;

    this.scrollToItem(selectedIndex);
  }

  scrollToItem(itemIndex) {
    const selectedItem = this.items[itemIndex];

    if (!(selectedItem instanceof HTMLElement)) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    selectedItem.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });

    this.setActiveIndicator(itemIndex);
  }

  updateActiveIndicator() {
    if (!(this.list instanceof HTMLElement)) return;

    const listRect = this.list.getBoundingClientRect();
    const listCenter = listRect.left + listRect.width / 2;
    let activeIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenter - listCenter);

      if (distance < closestDistance) {
        activeIndex = index;
        closestDistance = distance;
      }
    });

    this.setActiveIndicator(activeIndex);
  }

  setActiveIndicator(activeIndex) {
    this.indicators.forEach((indicator, index) => {
      const isActive = index === activeIndex;

      indicator.classList.toggle('mg-feature-card-grid__indicator--active', isActive);

      if (isActive) {
        indicator.setAttribute('aria-current', 'true');
      } else {
        indicator.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('feature-card-grid')) {
  customElements.define('feature-card-grid', FeatureCardGrid);
}
